/**
 * ONE answer to "is there unsaved work in this shell?" (#2328 §2/§5, ROOT ruling 74071 §10).
 *
 * ── the ruling, and why it takes two halves ────────────────────────────────
 * 74071 §10: *"Dirty-state protection requires both `beforeunload` and routed
 * navigation blocking, covering every exit #2328 lists."* They are different
 * mechanisms for different exits and neither substitutes for the other:
 *
 *   · `beforeunload` covers RELOAD, CLOSE and a navigation that leaves the
 *     document. The browser owns the dialog; a page can only say that there is
 *     something to lose.
 *   · a ROUTER blocker covers in-app navigation, which never unloads anything,
 *     so `beforeunload` does not fire at all for it.
 *   · the exits that are neither — persona switch, sign out, closing a routed
 *     overlay — are ordinary callbacks, and they ask this module directly
 *     through `confirmDiscardUnsaved()`.
 *
 * Measured on this base before writing: `useBlocker` has **0** occurrences in
 * `apps/console`, `apps/portal` and `packages/ui`, and the only `beforeunload`
 * interception is component-local to the navigation editor and already forked
 * into the portal. So this is one store rather than a third copy.
 *
 * ── why the store owns `beforeunload`, not a component ─────────────────────
 * A listener installed by a component is installed only while that component is
 * mounted, which makes the guard a property of the RENDER TREE rather than of
 * the unsaved work. The registry installs the listener when the first dirty key
 * arrives and removes it when the last one leaves, so a form that unmounts
 * without clearing cannot leave a permanent prompt behind, and a dirty form in
 * a closed drawer is still guarded.
 *
 * ── `packages/ui` may not know what a route is ─────────────────────────────
 * Deliberately router-free: the console has `@tanstack/react-router` and the
 * portal has **no router at all** (`ui-and-experience.md` §4). The routed half
 * is a host binding over this store — `apps/console/src/app/UnsavedChangesGuard.tsx`
 * — and the portal, which cannot route, still gets the unload half and the
 * imperative one from here.
 */
import { useEffect } from 'react'

/**
 * The sentence a non-browser exit shows. `beforeunload` cannot carry it —
 * every current browser ignores the string and shows its own — so it exists for
 * the exits a page DOES own: persona switch, sign out, closing an overlay.
 */
export const UNSAVED_PROMPT =
  'You have unsaved changes. Leave and discard them?'

/** Every surface currently holding unsaved work, by its own stable key. */
const dirty = new Set<string>()
const subs = new Set<() => void>()

/* Registered once, while anything is dirty. `preventDefault()` is the modern
   signal; `returnValue` is the legacy one and both are set because the pair is
   what every engine in the support matrix actually honours. */
function onBeforeUnload(event: BeforeUnloadEvent) {
  if (dirty.size === 0) return
  event.preventDefault()
  event.returnValue = ''
}

function sync() {
  if (typeof window === 'undefined') return
  if (dirty.size > 0) window.addEventListener('beforeunload', onBeforeUnload)
  else window.removeEventListener('beforeunload', onBeforeUnload)
}

/** This surface holds unsaved work. */
export function markUnsaved(key: string): void {
  if (dirty.has(key)) return
  dirty.add(key)
  sync()
  subs.forEach((fn) => fn())
}

/** This surface no longer holds unsaved work — saved, discarded or unmounted. */
export function clearUnsaved(key: string): void {
  if (!dirty.delete(key)) return
  sync()
  subs.forEach((fn) => fn())
}

/** Is anything in this shell unsaved? */
export function hasUnsavedChanges(): boolean {
  return dirty.size > 0
}

/** Which surfaces, for a host that wants to name them. Sorted, so it is stable. */
export function unsavedKeys(): string[] {
  return [...dirty].sort()
}

/** Subscribe to dirtiness changing — for a host blocker that re-reads it. */
export function subscribeUnsaved(cb: () => void): () => void {
  subs.add(cb)
  return () => { subs.delete(cb) }
}

/**
 * The guard for an exit the PAGE owns — persona switch, sign out, closing a
 * routed overlay. Returns true when it is safe to proceed.
 *
 * Clean ⇒ true with NO prompt: a confirmation nobody needed is how people learn
 * to dismiss confirmations without reading them.
 *
 * `confirm` is injectable so a test can drive both answers without a DOM, and
 * so a host may later replace the browser dialog with its own — the decision
 * stays "did the person agree", never "did a particular widget appear".
 */
export function confirmDiscardUnsaved(
  confirm: (message: string) => boolean = (message) =>
    typeof window === 'undefined' ? true : window.confirm(message),
): boolean {
  if (dirty.size === 0) return true
  return confirm(UNSAVED_PROMPT)
}

/**
 * Bind a surface's dirty flag to the registry for as long as it is mounted.
 *
 * The cleanup clears the key, so a form that navigates away mid-edit stops
 * guarding exits it no longer owns. The KEY is the surface's identity: two
 * dirty forms are two keys, and clearing one must not unguard the other.
 */
export function useUnsavedChanges(key: string, isDirty: boolean): void {
  useEffect(() => {
    if (isDirty) markUnsaved(key)
    else clearUnsaved(key)
  }, [key, isDirty])
  useEffect(() => () => { clearUnsaved(key) }, [key])
}

/** Test seam: forget everything. Never called by product code. */
export function resetUnsavedChanges(): void {
  dirty.clear()
  sync()
  subs.forEach((fn) => fn())
}
